import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';

import * as echarts from 'echarts/core';
import '../config/echarts_theme';

import { LineChart } from 'echarts/charts';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  MarkLineComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import localize from 'scaleflow/utils/localize';
import placeDecimal from 'scaleflow/utils/place-decimal';

echarts.use([
  LineChart,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  MarkLineComponent,
  CanvasRenderer,
]);

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatTargetDate(dateStr) {
  const d = new Date(dateStr);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function formatLabel(attrs) {
  const { period_type, period_number, year, date } = attrs;
  if (period_type === 'day') return date;
  if (period_type === 'week')
    return `${year}-W${String(period_number).padStart(2, '0')}`;
  if (period_type === 'month') return `${MONTHS[period_number - 1]} ${year}`;
  if (period_type === 'quarter') return `Q${period_number} ${year}`;
  return String(year);
}

function getPreciseValue(value, decimals = 2) {
  const abs = Math.abs(value);
  const preciseValue = placeDecimal(abs, decimals);
  return preciseValue;
}

// Parse a dataset (array of arrays) into plain objects keyed by the header row.
// Coerces year, period_number, and value to numbers.
function parseDataset(dataset, decimals = 2) {
  if (!dataset || dataset.length < 2) return [];
  const [fields, ...rows] = dataset;
  const yearIdx = fields.indexOf('year');
  const periodNumberIdx = fields.indexOf('period_number');
  const valueIdx = fields.indexOf('value');
  const periodTypeIdx = fields.indexOf('period_type');
  const dateIdx = fields.indexOf('date');
  return rows.map((row) => ({
    year: Number(row[yearIdx]),
    period_number:
      row[periodNumberIdx] != null && row[periodNumberIdx] !== ''
        ? Number(row[periodNumberIdx])
        : null,
    value:
      row[valueIdx] != null
        ? getPreciseValue(Number(row[valueIdx]), decimals)
        : null,
    period_type: row[periodTypeIdx],
    date: dateIdx !== -1 ? row[dateIdx] : undefined,
  }));
}

function cleanup(instance) {
  instance.chart?.dispose();
}

export default class MetricLineChartModifier extends Modifier {
  @service intl;
  @service organisation;

  chart = null;

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  // Called on insert and on every tracked-arg change.
  modify(element, [dataset = []], { targets = [], metric, mini = false }) {
    if (!this.chart) {
      this.chart = echarts.init(element, 'producthub', {
        height: mini ? 48 : 480,
      });
    }

    const metricModel = metric?.content ?? metric;
    const sorted = parseDataset(dataset, metricModel?.decimals ?? 2).sort(
      (a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return (a.period_number ?? 0) - (b.period_number ?? 0);
      },
    );

    this.chart.setOption(
      mini
        ? this._miniOptions(sorted)
        : this._fullOptions(sorted, targets, metric),
      true,
    );
  }

  // ─── Mini sparkline (no axes, no tooltip) ─────────────────────────────────

  _miniOptions(sorted) {
    const values = sorted.map((d) => d.value);
    return {
      animation: false,
      grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
      xAxis: {
        type: 'category',
        show: false,
        data: sorted.map((_, i) => i),
        boundaryGap: false,
      },
      yAxis: { type: 'value', show: false, scale: true },
      series: [
        {
          type: 'line',
          data: values,
          smooth: 0.4,
          symbol: 'none',
          lineStyle: { color: '#6366f1', width: 1.5 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(99,102,241,0.18)' },
                { offset: 1, color: 'rgba(99,102,241,0)' },
              ],
            },
          },
        },
      ],
    };
  }

  // ─── Full chart (axes, tooltip, target mark-lines) ────────────────────────

  _fullOptions(sorted, targets, metric) {
    const labels = sorted.map((d) => formatLabel(d));
    const values = sorted.map((d) => d.value);
    const dataMax = values.length > 0 ? Math.max(...values) : 0;

    const metricModel = metric?.content ?? metric;
    const maxTargetValue =
      targets.length > 0 ? Math.max(...targets.map((t) => t.target_value)) : 0;
    const decimals = metricModel?.decimals ?? 2;
    const symbol = metricModel?.symbol;
    const formatValue = (v) => {
      if (symbol === 'percentage')
        return localize(
          v,
          '%',
          undefined,
          this.organisation.locale,
          decimals,
          decimals,
        );
      if (symbol === 'currency')
        return localize(
          v,
          ',-',
          this.intl.t('currency.euro'),
          this.organisation.locale,
          decimals,
          decimals,
        );
      return localize(
        v,
        undefined,
        undefined,
        this.organisation.locale,
        decimals,
        decimals,
      );
    };

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#1e1b4b',
        borderColor: 'transparent',
        textStyle: { color: '#fff', fontSize: 12 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#6366f1', width: 1, type: 'dashed' },
        },
        formatter: (params) => {
          const point = params[0];
          const rows = [
            `<span style="font-size:11px;color:#9ca3af">${point?.axisValue ?? ''}</span>`,
            `<span>${point?.marker ?? ''}${formatValue(point?.value)}</span>`,
          ];
          if (targets.length > 0) {
            rows.push(
              `<span style="font-size:11px;color:#9ca3af;margin-top:2px">Targets:</span>`,
            );
            for (const t of targets) {
              const endDate = t.end_date ? formatTargetDate(t.end_date) : null;
              const isInitiative = !!t.initiative?.content?.title;
              const contextName =
                t.initiative?.content?.title ??
                t.goal?.content?.title ??
                'Target';
              const iconPath = isInitiative
                ? 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18ZM12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z'
                : 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z';
              const icon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;width:12px;height:12px;vertical-align:middle;margin-right:3px"><path d="${iconPath}"/></svg>`;
              rows.push(
                `<span style="color:#f59e0b;padding-left:8px">${icon}${contextName}: ${formatValue(t.target_value)}${endDate ? ` by ${endDate}` : ''}</span>`,
              );
            }
          }
          return rows.join('<br/>');
        },
      },
      grid: {
        left: '1%',
        right: '2%',
        top: '8%',
        bottom: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: labels,
        boundaryGap: false,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
        axisLabel: {
          rotate: labels.length > 12 ? 45 : 0,
          color: '#9ca3af',
          fontSize: 11,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        max:
          maxTargetValue > dataMax
            ? Math.ceil(maxTargetValue * 1.1)
            : undefined,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 11 },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'solid' } },
        splitArea: { show: false },
      },
      series: [
        {
          type: 'line',
          data: values,
          smooth: 0.4,
          symbol: 'circle',
          symbolSize: 5,
          showSymbol: labels.length <= 30,
          lineStyle: { color: '#6366f1', width: 2.5 },
          itemStyle: { color: '#6366f1', borderColor: '#fff', borderWidth: 2 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(99,102,241,0.18)' },
                { offset: 1, color: 'rgba(99,102,241,0)' },
              ],
            },
          },
          ...(targets.length > 0 && {
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: { color: '#f59e0b', type: 'dashed', width: 1.5 },
              label: { show: false },
              data: targets
                .filter((t) => t.target_value != null)
                .map((t) => ({ yAxis: t.target_value })),
            },
          }),
        },
      ],
    };
  }
}
